import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PricingSettingsProvider } from './contexts/PricingSettingsContext';
import { BroadcastsProvider } from './contexts/BroadcastsContext';
import { useMassCatalogSync } from './hooks/useMassCatalogSync';
import { PrivateRoute } from './routes/PrivateRoute';
import AdminViewingAsBanner from './components/common/AdminViewingAsBanner';
import TestModeBanner from './components/common/TestModeBanner';
import MaintenanceGate from './components/common/MaintenanceGate';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import DriverDashboard from './pages/driver/DriverDashboard';
import ActiveOrder from './pages/driver/ActiveOrder';
import Earnings from './pages/driver/Earnings';
import StripeRefresh from './pages/driver/StripeRefresh';
import DriverOrders from './pages/driver/DriverOrders';
import DriverProfile from './pages/driver/DriverProfile';
import OrdererDashboard from './pages/orderer/OrdererDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsPage from './pages/legal/TermsPage';
import OrdererProfile from './pages/driver/OrdererProfile';
import AddRole from './pages/profile/AddRole';
import NotificationsPage from './pages/common/NotificationsPage';
import FeedbackPage from './pages/common/FeedbackPage';
import AcceptInvitePage from './pages/orderer/AcceptInvitePage';
import CarrierDashboard from './pages/carrier/CarrierDashboard';
import CarrierInviteAccept from './pages/carrier/CarrierInviteAccept';
import PhoneLogin from './pages/auth/PhoneLogin';
import CarrierRegister from './pages/auth/CarrierRegister';

function RoleRedirect() {
  const { currentUser, effectiveRole } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (effectiveRole === 'driver')  return <Navigate to="/driver" replace />;
  if (effectiveRole === 'orderer') return <Navigate to="/orderer" replace />;
  if (effectiveRole === 'admin')   return <Navigate to="/admin" replace />;
  if (effectiveRole === 'carrier') return <Navigate to="/transportor" replace />;
  return <Navigate to="/login" replace />;
}

// Henter massekatalogen når brukeren er logget inn (må ligge under AuthProvider)
function MassCatalogSync() {
  const { currentUser } = useAuth();
  useMassCatalogSync(!!currentUser);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
        <MassCatalogSync />
        <PricingSettingsProvider>
          <BroadcastsProvider>
          <AdminViewingAsBanner />
          <TestModeBanner />
          <MaintenanceGate>
          <Routes>
            {/* Offentlige ruter */}
            <Route path="/login"      element={<Login />} />
            <Route path="/register"   element={<Register />} />
            <Route path="/personvern" element={<PrivacyPolicy />} />
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
            <Route path="/login/telefon" element={<PhoneLogin />} />
            <Route path="/register/transportor" element={<CarrierRegister />} />

            {/* Rollebaserte ruter */}
            <Route
              path="/driver"
              element={
                <PrivateRoute roles={['driver']}>
                  <DriverDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/active/:orderId"
              element={
                <PrivateRoute roles={['driver']}>
                  <ActiveOrder />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/earnings"
              element={
                <PrivateRoute roles={['driver']}>
                  <Earnings />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/stripe/refresh"
              element={
                <PrivateRoute roles={['driver']}>
                  <StripeRefresh />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/orders"
              element={
                <PrivateRoute roles={['driver']}>
                  <DriverOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/profil"
              element={
                <PrivateRoute roles={['driver']}>
                  <DriverProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/varsler"
              element={
                <PrivateRoute roles={['driver']}>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/tilbakemelding"
              element={
                <PrivateRoute roles={['driver']}>
                  <FeedbackPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/driver/orderer/:ordererId"
              element={
                <PrivateRoute roles={['driver']}>
                  <OrdererProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/orderer/*"
              element={
                <PrivateRoute roles={['orderer']}>
                  <OrdererDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <PrivateRoute roles={['admin']}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/transportor/varsler"
              element={
                <PrivateRoute roles={['carrier']}>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/transportor/*"
              element={
                <PrivateRoute roles={['carrier']}>
                  <CarrierDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/invitasjon/transportor/:orgId"
              element={
                <PrivateRoute roles={['driver', 'orderer', 'carrier', 'admin']}>
                  <CarrierInviteAccept />
                </PrivateRoute>
              }
            />

            <Route
              path="/vilkar"
              element={
                <PrivateRoute roles={['driver', 'orderer', 'carrier', 'admin']}>
                  <TermsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile/add-role"
              element={
                <PrivateRoute roles={['driver', 'orderer', 'admin']}>
                  <AddRole />
                </PrivateRoute>
              }
            />

            {/* Rot-URL sender til riktig startside basert på rolle */}
            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </MaintenanceGate>
          </BroadcastsProvider>
        </PricingSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}
